import Assert from '../../../../../utils/asserts';
import { REPORT_PROPERTIES, REPORT_PROP, COLUMN_TYPE } from '../../constants/generalConstants';
import { CellIdType, ColIdType, ColumnMetadataType, RowIdType, RowMetadataType, TableBlockType } from '../../types';
import { UNSUPPORTED_FOR_IMPORT_COLUMN_TYPES, UNSUPPORTED_FOR_IMPORT_ROW_TYPES } from './constants';

function isMetadataValid(reportsMetadata: ColumnMetadataType[]): boolean {
    let hasUnfilled = false;
    for (const reportMetadata of reportsMetadata) {
        hasUnfilled = REPORT_PROPERTIES.filter((x) => x !== REPORT_PROP.SCALE).some(
            (prop) => !reportMetadata.reportProps![prop]
        );
        if (hasUnfilled) {
            break;
        }
    }

    return hasUnfilled;
}

export function hasDuplicateReports(reportsMetadata: ColumnMetadataType[]): boolean {
    const reportsMap: Record<string, number> = {};

    for (const reportMetadata of reportsMetadata) {
        const { scale, ...rest } = reportMetadata.reportProps!;
        Assert.trueVal(reportMetadata.colId.includes('TABLE'), 'TABLE keyword is not present in column ID!');
        /** TODO: IMPORTANT: We should not rely on the col id to get the table id this should be fixed */
        const tableId = getTableIdFromMetadataEntityId(reportMetadata.colId);
        const key = `${tableId}${rest.format}${rest.period}${rest.version}${rest.year}`;
        if (!reportsMap[key]) {
            reportsMap[key] = 0;
        }

        reportsMap[key] += 1;
    }
    return Object.values(reportsMap).some((value) => value > 1);
}

type ValidationResultType = { message: string; error: boolean };

export function validateReports(reportsMetadata: ColumnMetadataType[]): ValidationResultType {
    const result: ValidationResultType = { message: '', error: false };
    if (isMetadataValid(reportsMetadata)) {
        result.message = 'Please fill out all of the fields for the reports!';
        result.error = true;
    } else if (hasDuplicateReports(reportsMetadata)) {
        result.message = 'There are duplicate reports present!';
        result.error = true;
    }

    return result;
}

function hasUnsupportedColumnTypes(colsMetadata: ColumnMetadataType[]) {
    return colsMetadata.some(
        (colMetadata) =>
            colMetadata.importCandidate && UNSUPPORTED_FOR_IMPORT_COLUMN_TYPES.includes(colMetadata.contentType)
    );
}

function hasUnsupportedRowTypes(rowsMetadata: RowMetadataType[]) {
    return rowsMetadata.some(
        (rowMetadata) =>
            rowMetadata.importCandidate && UNSUPPORTED_FOR_IMPORT_ROW_TYPES.includes(rowMetadata.contentType)
    );
}

export function validateTables(
    tables: TableBlockType[],
    rowsMetadata: RowMetadataType[],
    colsMetadata: ColumnMetadataType[]
): ValidationResultType {
    const result: ValidationResultType = { message: '', error: false };

    if (hasUnsupportedColumnTypes(colsMetadata)) {
        result.message = `Tables have one or more unsupported column types for import. The following types are NOT supported: ${UNSUPPORTED_FOR_IMPORT_COLUMN_TYPES.join(
            ', '
        )}`;
        result.error = true;
    } else if (hasUnsupportedRowTypes(rowsMetadata)) {
        result.message = `Tables have one or more unsupported row types for import.The following types are NOT supported: ${UNSUPPORTED_FOR_IMPORT_ROW_TYPES.join(
            ', '
        )}`;
        result.error = true;
    } else {
        for (const table of tables) {
            const hasRowImportCandidate = rowsMetadata.some(
                (rowMetadata) => rowMetadata.importCandidate && rowMetadata.rowId.includes(table.id)
            );

            const hasColImportCandidate = colsMetadata.some(
                (colMetadata) => colMetadata.importCandidate && colMetadata.colId.includes(table.id)
            );

            const hasFieldNamesColumn = colsMetadata.some(
                (colMetadata) =>
                    colMetadata.importCandidate &&
                    colMetadata.colId.includes(table.id) &&
                    colMetadata.contentType === COLUMN_TYPE.FIELD_NAMES
            );

            if ((hasRowImportCandidate || hasColImportCandidate) && !hasFieldNamesColumn) {
                result.message = 'Every table with import data must contain FIELD_NAMES column.';
                result.error = true;
                break;
            } else if (hasRowImportCandidate !== hasColImportCandidate) {
                result.message = 'To import data from a table, both rows and columns must be selected.';
                result.error = true;
                break;
            }
        }
    }

    return result;
}

export function getTableIdFromMetadataEntityId(id: RowIdType | ColIdType | CellIdType) {
    return id.split('TABLE:')[1];
}
